import * as React from 'react';
import { Checkbox } from '../../../ui/checkbox';
import { Label } from '../../../ui/label';
import { cn } from '../../../../lib/utils';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../ui/alert-dialog';

interface DocumentCheckboxProps {
    label?: string;
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    disabled?: boolean;
    className?: string;
    id?: string;
    confirmation?: {
        title: string;
        message: string;
        confirmText?: string;
    };
}

export function DocumentCheckbox({
    label,
    checked,
    onCheckedChange,
    disabled,
    className,
    id,
    confirmation,
}: DocumentCheckboxProps) {
    const generatedId = React.useMemo(
        () => id || `checkbox-${Math.random().toString(36).substr(2, 9)}`,
        [id]
    );
    const [showConfirm, setShowConfirm] = React.useState(false);

    const handleCheckedChange = (newChecked: boolean) => {
        if (newChecked && confirmation && !checked) {
            setShowConfirm(true);
        } else {
            onCheckedChange?.(newChecked);
        }
    };

    const handleConfirm = () => {
        onCheckedChange?.(true);
        setShowConfirm(false);
    };

    return (
        <>
            <div className={cn('flex items-start space-x-2', className)}>
                <Checkbox
                    id={generatedId}
                    checked={checked}
                    onCheckedChange={handleCheckedChange}
                    disabled={disabled}
                    className="mt-0.5"
                />
                {label && (
                    <Label
                        htmlFor={generatedId}
                        className="text-sm font-medium leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                        {label}
                    </Label>
                )}
            </div>

            {confirmation && (
                <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {confirmation.title}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {confirmation.message}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleConfirm}>
                                {confirmation.confirmText || 'Confirm'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </>
    );
}
